// AUTO-GENERATED by cmd/apigen — DO NOT EDIT
// Source of truth: Go structs in internal/api/*.go
// Regenerate: make apigen

import axios from 'axios'

// ============================================================================
// Types (generated from Go structs)
// ============================================================================

export interface ActiveSession {
  token_prefix: string
  ip_address: string
  created_at: string
  expires_at: string
  totp_verified: boolean
  current: boolean
}

export interface ActiveUser {
  username: string
  session_count: number
}

export interface BrandingResponse {
  site_name: string
  has_logo: boolean
}

export interface ChangePasswordRequest {
  current_password: string
  new_password: string
}

export interface ClientConfig {
  site_name: string
  server_domain: string
  sftp_port: number
  allow_folder_rename: boolean
  folder_rename_warn_threshold: number
  allow_recursive_delete: boolean
  allow_drag_drop_move: boolean
  proxy_mode: boolean
}

export interface ConfigResponse {
  enabled: boolean
  entity_name: string
  entity_name_plural: string
  label_format: string
  colors: string[]
  tags: Tag[]
}

export interface CreateGroupRequest {
  name: string
  description: string
}

export interface CreateUserRequest {
  username: string
  password: string
  site_permission: string
}

export interface CreateUserResponse {
  id: number
  username: string
  site_permission: string
}

export interface CreateViewRequest {
  name: string
  base_path: string
  description: string
  personal?: boolean
}

export interface DeviceResponse {
  device_id: string
  device_name: string
  registered_at: string
  last_used: string
  is_current: boolean
  password?: string
}

export interface DisableTOTPRequest {
  password: string
}

export interface EnableTOTPRequest {
  password: string
}

export interface EnableTOTPResponse {
  qr_code: string
  secret: string
  backup_codes: string[]
}

export interface ErrorResponse {
  error: string
}

export interface FolderRequest {
  path: string
  group_id: number
}

export interface GeneratePasswordResponse {
  password: string
}

export interface GroupResponse {
  id: number
  name: string
  s3_location: string
  description: string
  created_at: string
  user_role: string
  permissions: number
  registry_enabled: boolean
  require_2fa: boolean
}

export interface ListFilesResponse {
  path: string
  files: S3Object[]
  total: number
  offset: number
  limit: number
  has_more: boolean
  group_id?: number
  group_name?: string
  group_role?: string
  group_perms?: number
  view_id?: number
  registry_enabled?: boolean
  groups?: RootGroupEntry[]
}

export interface ListUsersResponse {
  users: UserResponse[]
}

export interface LoginRequest {
  username: string
  password: string
}

// Login returns either LoginResponse or TwoFARequiredResponse
export type LoginResult = LoginResponse | TwoFARequiredResponse

export interface LoginResponse {
  token: string
  expires_at: string
}

export interface MemberResponse {
  user_id: number
  username: string
  role: string
  can_delete: boolean
  can_upload: boolean
  can_download: boolean
  can_list: boolean
  can_rename: boolean
  can_manage_vals: boolean
  created_at: string
}

export interface MessageResponse {
  message: string
}

export interface MoveRequest {
  source_path: string
  target_path: string
  group_id: number
  preserve_path: boolean
}

export interface PermissionJSON {
  can_delete: boolean
  can_upload: boolean
  can_download: boolean
  can_list: boolean
  can_rename: boolean
  can_manage_vals: boolean
}

export interface PresignedURLRequest {
  path: string
  group_id: number
  content_type: string
  inline?: boolean
}

export interface PresignedURLResponse {
  url: string
  expires_at: string
  final_path?: string
}

export interface RecordPage {
  records: RecordRow[]
  has_more: boolean
  cursor?: string
}

export interface RecordRow {
  id: number
  group_id: number
  path: string
  comment: string
  color: string
  creator: number
  updator: number
  created_at: string
  updated_at: string
  deleted_at?: string
  value_ids?: number[]
  label?: string
  tag_values: Record<string, string[]>
  updated_by: string
}

export interface RegisterDeviceResponse {
  device_id: string
  device_name: string
  webdav_password: string
}

export interface RegistryRecord {
  id: number
  group_id: number
  path: string
  comment: string
  color: string
  creator: number
  updator: number
  created_at: string
  updated_at: string
  deleted_at?: string
  value_ids?: number[]
  label?: string
}

export interface RenameRequest {
  old_path: string
  new_path: string
  group_id: number
  confirm: boolean
}

export interface RenameResponse {
  success: boolean
  needs_confirmation: boolean
  item_count: number
}

export interface ResetPasswordRequest {
  new_password: string
}

export interface RootGroupEntry {
  id: number
  name: string
  mount_name: string
  role: string
  perms: number
  view_id?: number
  base_path?: string
  registry_enabled?: boolean
}

export interface S3Object {
  key: string
  size: number
  last_modified: string
  etag: string
  content_type: string
  is_dir: boolean
}

export interface SessionResponse {
  user_id: number
  username: string
  site_permission: string
  totp_enabled: boolean
  totp_verified: boolean
  user_permissions: UserPermissionJSON
  valid: boolean
}

export interface SuccessResponse {
  success: boolean
}

export interface TOTPStatusResponse {
  status: string
  enabled: boolean
  backup_codes_remaining: number
}

export interface Tag {
  id: number
  name: string
  required: boolean
  editable: boolean
  multiple: boolean
  sort_order: number
  creator: number
  updator: number
  created_at: string
  updated_at: string
}

export interface TwoFARequiredResponse {
  requires_2fa: boolean
  temp_token: string
  webauthn_available?: boolean
}

export interface UnclaimedGroup {
  id: number
  name: string
  s3_location: string
  created_at: string
  status: string
  last_admin: string
  member_count: number
}

export interface UnclaimedGroupsResponse {
  groups: UnclaimedGroup[]
}

export interface UpdateGroupRequest {
  name: string
  description: string
  registry_enabled?: boolean
  require_2fa?: boolean
}

export interface UpdatePermissionRequest {
  site_permission: string
}

export interface UserPermissionJSON {
  can_mount: boolean
  can_create_group: boolean
  can_edit_tags: boolean
  can_edit_vals: boolean
}

export interface UserResponse {
  id: number
  username: string
  site_permission: string
  totp_enabled: boolean
  webauthn_count: number
  created_at: string
  storage_used: number
  file_count: number
  deleted: boolean
  user_permissions: UserPermissionJSON
  device_count: number
  download_limit_hourly: number
  download_limit_daily: number
}

export interface Value {
  id: number
  tag_id: number
  group_id?: number
  value: string
  persistent: boolean
  creator: number
  updator: number
  created_at: string
  updated_at: string
}

export interface VerifyTOTPRequest {
  code: string
}

export interface VerifyTOTPResponse {
  status: string
  token: string
  backup_code_used: boolean
  remaining_codes: number
}

export interface ViewInfoResponse {
  group_admins: string[]
}

export interface ViewMemberResponse {
  user_id: number
  username: string
  can_delete: boolean
  can_upload: boolean
  can_download: boolean
  can_list: boolean
  can_rename: boolean
  can_manage_vals: boolean
  created_at: string
}

export interface ViewResponse {
  id: number
  group_id: number
  name: string
  base_path: string
  description: string
  created_at: string
  created_by: number
}

export interface recordRequest {
  group_id: number
  comment: string
  color: string
  value_ids: number[]
}

export interface tagRequest {
  name: string
  required: boolean
  editable: boolean
  multiple: boolean
  sort_order: number
}

export interface valueRequest {
  value: string
  persistent: boolean
  group_id: number
}

// ============================================================================
// Derived types
// ============================================================================

export interface User {
  id: number
  username: string
  isUserAdmin: boolean
  permissions?: {
    canEditUsers: boolean
    canEditGroups: boolean
    mountEnabled: boolean
    canCreateGroup: boolean
    canEditTags: boolean
    canEditVals: boolean
  }
}

export function sessionToUser(s: SessionResponse): User {
  return {
    id: s.user_id,
    username: s.username,
    isUserAdmin: s.site_permission === 'user-admin',
    permissions: {
      canEditUsers: s.site_permission === 'user-admin',
      canEditGroups: s.site_permission === 'user-admin',
      mountEnabled: s.user_permissions?.can_mount ?? false,
      canCreateGroup: s.user_permissions?.can_create_group ?? false,
      canEditTags: s.user_permissions?.can_edit_tags ?? false,
      canEditVals: s.user_permissions?.can_edit_vals ?? false,
    },
  }
}

export function fileName(f: S3Object): string {
  const k = f.key.replace(/\/$/, '')
  const idx = k.lastIndexOf('/')
  return idx >= 0 ? k.substring(idx + 1) : k
}

// ============================================================================
// API Client
// ============================================================================

const client = axios.create({
  baseURL: '/api',
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' },
})

export { client }

export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.error || error.message
  }
  if (error instanceof Error) return error.message
  return 'An unknown error occurred'
}

// ============================================================================
// API Functions (generated from Go route definitions)
// ============================================================================

export const api = {
  /** Get site branding — GET /api/branding */
  getBranding: async (): Promise<BrandingResponse> => {
    const { data } = await client.get<BrandingResponse>('/branding')
    return data
  },

  /** Authenticate user — POST /api/login */
  login: async (req: LoginRequest): Promise<LoginResult> => {
    const { data } = await client.post<LoginResult>('/login', req)
    return data
  },

  /** Revoke token — POST /api/logout */
  logout: async (): Promise<void> => {
    await client.post('/logout')
  },

  /** Get current session — GET /api/session */
  getSession: async (): Promise<SessionResponse> => {
    const { data } = await client.get<SessionResponse>('/session')
    return data
  },

  /** Change own password — POST /api/change-password */
  changePassword: async (req: ChangePasswordRequest): Promise<SuccessResponse> => {
    const { data } = await client.post<SuccessResponse>('/change-password', req)
    return data
  },

  /** Get 2FA status — GET /api/2fa/status */
  get2FAStatus: async (): Promise<TOTPStatusResponse> => {
    const { data } = await client.get<TOTPStatusResponse>('/2fa/status')
    return data
  },

  /** Enable 2FA — POST /api/2fa/enable */
  enable2FA: async (req: EnableTOTPRequest): Promise<EnableTOTPResponse> => {
    const { data } = await client.post<EnableTOTPResponse>('/2fa/enable', req)
    return data
  },

  /** Verify 2FA code — POST /api/2fa/verify */
  verify2FA: async (req: VerifyTOTPRequest): Promise<VerifyTOTPResponse> => {
    const { data } = await client.post<VerifyTOTPResponse>('/2fa/verify', req)
    return data
  },

  /** Disable 2FA — POST /api/2fa/disable */
  disable2FA: async (req: DisableTOTPRequest): Promise<TOTPStatusResponse> => {
    const { data } = await client.post<TOTPStatusResponse>('/2fa/disable', req)
    return data
  },

  /** Get presigned upload URL — POST /api/files/upload-url */
  getUploadUrl: async (req: PresignedURLRequest): Promise<PresignedURLResponse> => {
    const { data } = await client.post<PresignedURLResponse>('/files/upload-url', req)
    return data
  },

  /** Get presigned download URL — POST /api/files/download-url */
  getDownloadUrl: async (req: PresignedURLRequest): Promise<PresignedURLResponse> => {
    const { data } = await client.post<PresignedURLResponse>('/files/download-url', req)
    return data
  },

  /** Create folder — POST /api/files/folder */
  createFolder: async (req: FolderRequest): Promise<SuccessResponse> => {
    const { data } = await client.post<SuccessResponse>('/files/folder', req)
    return data
  },

  /** Rename file or folder — POST /api/files/rename */
  renameFile: async (req: RenameRequest): Promise<RenameResponse> => {
    const { data } = await client.post<RenameResponse>('/files/rename', req)
    return data
  },

  /** Move file — POST /api/files/move */
  moveFile: async (req: MoveRequest): Promise<RenameResponse> => {
    const { data } = await client.post<RenameResponse>('/files/move', req)
    return data
  },

  /** Get file browser config — GET /api/files/config */
  getFileConfig: async (): Promise<ClientConfig> => {
    const { data } = await client.get<ClientConfig>('/files/config')
    return data
  },

  /** List all users — GET /api/admin/users */
  adminListUsers: async (): Promise<ListUsersResponse> => {
    const { data } = await client.get<ListUsersResponse>('/admin/users')
    return data
  },

  /** List deleted users — GET /api/admin/users/deleted */
  adminListDeletedUsers: async (): Promise<ListUsersResponse> => {
    const { data } = await client.get<ListUsersResponse>('/admin/users/deleted')
    return data
  },

  /** Create user — POST /api/admin/users/create */
  adminCreateUser: async (req: CreateUserRequest): Promise<CreateUserResponse> => {
    const { data } = await client.post<CreateUserResponse>('/admin/users/create', req)
    return data
  },

  /** Create group — POST /api/groups/create */
  createGroup: async (req: CreateGroupRequest): Promise<GroupResponse> => {
    const { data } = await client.post<GroupResponse>('/groups/create', req)
    return data
  },

  /** Get registry config — GET /api/registry/config */
  getRegistryConfig: async (): Promise<ConfigResponse> => {
    const { data } = await client.get<ConfigResponse>('/registry/config')
    return data
  },

  /** List all tags — GET /api/registry/tags */
  listTags: async (): Promise<Tag[]> => {
    const { data } = await client.get<Tag[]>('/registry/tags')
    return data
  },

  /** Create tag — POST /api/registry/tags */
  createTag: async (req: tagRequest): Promise<Tag> => {
    const { data } = await client.post<Tag>('/registry/tags', req)
    return data
  },

  // --- Endpoints with path parameters ---

  /** List files — GET /api/files/list */
  listFiles: async (params: Record<string, string>): Promise<ListFilesResponse> => {
    const { data } = await client.get<ListFilesResponse>('/files/list', { params })
    return data
  },

  /** Delete file — DELETE /api/files/delete */
  deleteFile: async (req: PresignedURLRequest): Promise<SuccessResponse> => {
    const { data } = await client.delete<SuccessResponse>('/files/delete', { data: req })
    return data
  },

  /** Delete folder — DELETE /api/files/folder-delete */
  deleteFolder: async (req: FolderRequest): Promise<SuccessResponse> => {
    const { data } = await client.delete<SuccessResponse>('/files/folder-delete', { data: req })
    return data
  },

  /** Delete user — DELETE /api/admin/users/delete/{id} */
  adminDeleteUser: async (id: number): Promise<SuccessResponse> => {
    const { data } = await client.delete<SuccessResponse>(`/admin/users/delete/${id}`)
    return data
  },

  /** Restore user — POST /api/admin/users/restore/{id} */
  adminRestoreUser: async (id: number): Promise<SuccessResponse> => {
    const { data } = await client.post<SuccessResponse>(`/admin/users/restore/${id}`)
    return data
  },

  /** Update permission — PUT /api/admin/users/permission/{id} */
  adminUpdatePermission: async (id: number, req: UpdatePermissionRequest): Promise<SuccessResponse> => {
    const { data } = await client.put<SuccessResponse>(`/admin/users/permission/${id}`, req)
    return data
  },

  /** Reset password — POST /api/admin/users/reset-password/{id} */
  adminResetPassword: async (id: number, req: ResetPasswordRequest): Promise<SuccessResponse> => {
    const { data } = await client.post<SuccessResponse>(`/admin/users/reset-password/${id}`, req)
    return data
  },

  /** Disable 2FA — POST /api/admin/users/disable-2fa/{id} */
  adminDisable2FA: async (id: number): Promise<SuccessResponse> => {
    const { data } = await client.post<SuccessResponse>(`/admin/users/disable-2fa/${id}`)
    return data
  },

  /** Set user permissions — PUT /api/admin/users/{id}/permissions */
  adminSetUserPermissions: async (id: number, perms: UserPermissionJSON): Promise<SuccessResponse> => {
    const { data } = await client.put<SuccessResponse>(`/admin/users/${id}/permissions`, perms)
    return data
  },

  /** Get user devices — GET /api/admin/users/{id}/devices */
  adminGetUserDevices: async (id: number): Promise<DeviceResponse[]> => {
    const { data } = await client.get<DeviceResponse[]>(`/admin/users/${id}/devices`)
    return data
  },

  /** Revoke user device — DELETE /api/admin/users/{userId}/devices/{deviceId} */
  adminRevokeUserDevice: async (userId: number, deviceId: string): Promise<void> => {
    await client.delete(`/admin/users/${userId}/devices/${deviceId}`)
  },

  /** List groups — GET /api/groups */
  listGroups: async (): Promise<GroupResponse[]> => {
    const { data } = await client.get<GroupResponse[]>('/groups')
    return data
  },

  /** Get group — GET /api/groups/{id} */
  getGroup: async (id: number): Promise<GroupResponse> => {
    const { data } = await client.get<GroupResponse>(`/groups/${id}`)
    return data
  },

  /** Update group — PUT /api/groups/{id} */
  updateGroup: async (id: number, req: UpdateGroupRequest): Promise<MessageResponse> => {
    const { data } = await client.put<MessageResponse>(`/groups/${id}`, req)
    return data
  },

  /** Delete group — DELETE /api/groups/{id} */
  deleteGroup: async (id: number): Promise<MessageResponse> => {
    const { data } = await client.delete<MessageResponse>(`/groups/${id}`)
    return data
  },

  /** List group members — GET /api/groups/{id}/members */
  listGroupMembers: async (id: number): Promise<MemberResponse[]> => {
    const { data } = await client.get<MemberResponse[]>(`/groups/${id}/members`)
    return data
  },

  /** Add group member — POST /api/groups/{id}/members */
  addGroupMember: async (id: number, req: { username: string; role: string }): Promise<MessageResponse> => {
    const { data } = await client.post<MessageResponse>(`/groups/${id}/members`, req)
    return data
  },

  /** Remove group member — DELETE /api/groups/{groupId}/members/{userId} */
  removeGroupMember: async (groupId: number, userId: number): Promise<MessageResponse> => {
    const { data } = await client.delete<MessageResponse>(`/groups/${groupId}/members/${userId}`)
    return data
  },

  /** Update member role — PUT /api/groups/{groupId}/members/{userId}/role */
  updateMemberRole: async (groupId: number, userId: number, req: { role: string }): Promise<MessageResponse> => {
    const { data } = await client.put<MessageResponse>(`/groups/${groupId}/members/${userId}/role`, req)
    return data
  },

  /** Update member permissions — PUT /api/groups/{groupId}/members/{userId}/permissions */
  updateMemberPermissions: async (groupId: number, userId: number, req: PermissionJSON): Promise<MessageResponse> => {
    const { data } = await client.put<MessageResponse>(`/groups/${groupId}/members/${userId}/permissions`, req)
    return data
  },

  /** List group views — GET /api/groups/{id}/views */
  listGroupViews: async (groupId: number): Promise<ViewResponse[]> => {
    const { data } = await client.get<ViewResponse[]>(`/groups/${groupId}/views`)
    return data
  },

  /** Create view — POST /api/groups/{id}/views */
  createView: async (groupId: number, req: CreateViewRequest): Promise<ViewResponse> => {
    const { data } = await client.post<ViewResponse>(`/groups/${groupId}/views`, req)
    return data
  },

  /** Get view — GET /api/views/{viewId} */
  getView: async (viewId: number): Promise<ViewResponse> => {
    const { data } = await client.get<ViewResponse>(`/views/${viewId}`)
    return data
  },

  /** Get view info — GET /api/views/{viewId}/info */
  getViewInfo: async (viewId: number): Promise<ViewInfoResponse> => {
    const { data } = await client.get<ViewInfoResponse>(`/views/${viewId}/info`)
    return data
  },

  /** Update view — PUT /api/views/{viewId} */
  updateView: async (viewId: number, req: CreateViewRequest): Promise<MessageResponse> => {
    const { data } = await client.put<MessageResponse>(`/views/${viewId}`, req)
    return data
  },

  /** Delete view — DELETE /api/views/{viewId} */
  deleteView: async (viewId: number): Promise<MessageResponse> => {
    const { data } = await client.delete<MessageResponse>(`/views/${viewId}`)
    return data
  },

  /** List view members — GET /api/views/{viewId}/members */
  listViewMembers: async (viewId: number): Promise<ViewMemberResponse[]> => {
    const { data } = await client.get<ViewMemberResponse[]>(`/views/${viewId}/members`)
    return data
  },

  /** Add view member — POST /api/views/{viewId}/members */
  addViewMember: async (viewId: number, req: { username: string; permissions: PermissionJSON }): Promise<MessageResponse> => {
    const { data } = await client.post<MessageResponse>(`/views/${viewId}/members`, req)
    return data
  },

  /** Remove view member — DELETE /api/views/{viewId}/members/{userId} */
  removeViewMember: async (viewId: number, userId: number): Promise<MessageResponse> => {
    const { data } = await client.delete<MessageResponse>(`/views/${viewId}/members/${userId}`)
    return data
  },

  /** Update view member permissions — PUT /api/views/{viewId}/members/{userId}/permissions */
  updateViewMemberPermissions: async (viewId: number, userId: number, req: PermissionJSON): Promise<MessageResponse> => {
    const { data } = await client.put<MessageResponse>(`/views/${viewId}/members/${userId}/permissions`, req)
    return data
  },

  /** List devices — GET /api/devices */
  listDevices: async (): Promise<DeviceResponse[]> => {
    const { data } = await client.get<DeviceResponse[]>('/devices')
    return data
  },

  /** Register device — POST /api/devices/register */
  registerDevice: async (req: { browser: string; os: string; user_agent: string }): Promise<RegisterDeviceResponse> => {
    const { data } = await client.post<RegisterDeviceResponse>('/devices/register', req)
    return data
  },

  /** Generate device password — POST /api/devices/password */
  generateDevicePassword: async (): Promise<GeneratePasswordResponse> => {
    const { data } = await client.post<GeneratePasswordResponse>('/devices/password')
    return data
  },

  /** Revoke device — DELETE /api/devices/{deviceId} */
  revokeDevice: async (deviceId: string): Promise<void> => {
    await client.delete(`/devices/${deviceId}`)
  },

  /** List all users (any authenticated user) — GET /api/users */
  listAllUsers: async (): Promise<ListUsersResponse> => {
    const { data } = await client.get<ListUsersResponse>('/users')
    return data
  },

  /** Update tag — PUT /api/registry/tags/{id} */
  updateTag: async (id: number, req: tagRequest): Promise<Tag> => {
    const { data } = await client.put<Tag>(`/registry/tags/${id}`, req)
    return data
  },

  /** Delete tag — DELETE /api/registry/tags/{id} */
  deleteTag: async (id: number): Promise<void> => {
    await client.delete(`/registry/tags/${id}`)
  },

  /** List values for tag — GET /api/registry/tags/{id}/values */
  listValues: async (tagId: number, groupId?: number): Promise<Value[]> => {
    const params = groupId ? `?group_id=${groupId}` : ''
    const { data } = await client.get<Value[]>(`/registry/tags/${tagId}/values${params}`)
    return data
  },

  /** Create value — POST /api/registry/tags/{id}/values */
  createValue: async (tagId: number, req: valueRequest): Promise<Value> => {
    const { data } = await client.post<Value>(`/registry/tags/${tagId}/values`, req)
    return data
  },

  /** Update value — PUT /api/registry/values/{id} */
  updateValue: async (id: number, req: valueRequest): Promise<Value> => {
    const { data } = await client.put<Value>(`/registry/values/${id}`, req)
    return data
  },

  /** Delete value — DELETE /api/registry/values/{id} */
  deleteValue: async (id: number): Promise<void> => {
    await client.delete(`/registry/values/${id}`)
  },

  /** Purge unused values — POST /api/registry/tags/{id}/purge */
  purgeValues: async (tagId: number): Promise<{ purged: number }> => {
    const { data } = await client.post<{ purged: number }>(`/registry/tags/${tagId}/purge`)
    return data
  },

  /** List records — GET /api/registry/records?group_id={id}&cursor=X&limit=N */
  listRecords: async (groupId: number, cursor = '', limit = 100): Promise<RecordPage> => {
    const { data } = await client.get<RecordPage>(`/registry/records`, { params: { group_id: groupId, cursor: cursor || undefined, limit } })
    return data
  },

  /** Create record — POST /api/registry/records */
  createRecord: async (req: recordRequest): Promise<RegistryRecord> => {
    const { data } = await client.post<RegistryRecord>('/registry/records', req)
    return data
  },

  /** Get record — GET /api/registry/records/{id} */
  getRecord: async (id: number): Promise<RegistryRecord> => {
    const { data } = await client.get<RegistryRecord>(`/registry/records/${id}`)
    return data
  },

  /** Update record — PUT /api/registry/records/{id} */
  updateRecord: async (id: number, req: recordRequest): Promise<RegistryRecord> => {
    const { data } = await client.put<RegistryRecord>(`/registry/records/${id}`, req)
    return data
  },

  /** Delete record — DELETE /api/registry/records/{id} */
  deleteRecord: async (id: number): Promise<void> => {
    await client.delete(`/registry/records/${id}`)
  },

  /** List own sessions — GET /api/auth/sessions */
  listSessions: async (): Promise<ActiveSession[]> => {
    const { data } = await client.get<ActiveSession[]>('/auth/sessions')
    return data
  },

  /** Revoke session — DELETE /api/auth/sessions?prefix={prefix} */
  revokeSession: async (prefix: string): Promise<void> => {
    await client.delete('/auth/sessions', { params: { prefix } })
  },

  /** List active users (admin) — GET /api/admin/active-sessions */
  listActiveUsers: async (): Promise<ActiveUser[]> => {
    const { data } = await client.get<ActiveUser[]>('/admin/active-sessions')
    return data
  },

}
